'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isShallowEqual } from '@interxfi/utilities/equality/shallow';
import type { Func } from '@interxfi/utilities/types/function';

interface IntersectionObserverOptions extends IntersectionObserverInit {
  disabled?: boolean;
}

export function useIntersectionObserverAction(intersectionAction: Func | undefined, options?: IntersectionObserverOptions) {
  const [parentRef, setParentRef] = useState<HTMLElement | null>(null);
  const [observedItemRef, setObservedItemRef] = useState<HTMLElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [cachedOptions, setCachedOptions] = useState(options);

  useEffect(() => {
    setCachedOptions((prevOptions) => {
      if (isShallowEqual(prevOptions, options)) {
        return prevOptions;
      }

      return options;
    });
  }, [options]);

  useLayoutEffect(() => {
    const { disabled, ...intersectionObserverInit } = cachedOptions || {};
    let localObserver: IntersectionObserver;

    if (!disabled && intersectionAction && observedItemRef) {
      localObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            intersectionAction();
          }
        },
        { root: parentRef || window.document, rootMargin: '200px', threshold: 0.1, ...intersectionObserverInit },
      );

      localObserver.observe(observedItemRef);

      observer.current = localObserver;
    }

    return () => {
      localObserver?.disconnect();
      observer.current = null;
    };
  }, [observedItemRef, parentRef, intersectionAction, cachedOptions]);

  return useMemo(() => [setObservedItemRef, setParentRef], []);
}
